import type { IntentActions, PropertySearchActions } from './actions';
import type { PropertyFiltersApi } from './context';
import type { PlaceSelection } from './intents';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { RouteLocationRaw } from 'vue-router';

import { cleanLocationQueryFromPath } from '~/utils/cleanLocationQueryFromPath';

import { bindIntents } from './actions';
import { nextFiltersForAreaSelection } from './intents';

export interface LegacyActionsDeps {
	api: Pick<
		PropertyFiltersApi,
		'getPathFromFilters' | 'getFiltersFromPath' | 'DEFAULT_URL_PATH'
	>;
	currentUrl: () => string;
	navigate: (to: RouteLocationRaw) => Promise<unknown>;
}

// Production writes, kept exactly as the components did them before the lab.
export function createLegacySearchActions(
	deps: LegacyActionsDeps
): PropertySearchActions {
	const current = () => deps.api.getFiltersFromPath(deps.currentUrl());
	return {
		...bindIntents(current, ({ filters }) =>
			deps.navigate(deps.api.getPathFromFilters(filters))
		),
		...legacySuggestActions(deps),
		selectAreas: (base, h3Ids, viewport) =>
			pushWithLocation(
				deps,
				nextFiltersForAreaSelection(base, h3Ids, viewport).filters
			),
		clear: () => deps.navigate(deps.api.DEFAULT_URL_PATH),
		takeMapUpdate: () => undefined
	};
}

function pushWithLocation(deps: LegacyActionsDeps, filters: PropertyFilters) {
	const location = {
		regions: filters.regions,
		cities: filters.cities,
		states: filters.states,
		macroregions: filters.macroregions
	};
	return deps.navigate(
		deps.api.getPathFromFilters(filters, location, { page: 'imoveis' })
	);
}

function legacySuggestActions(
	deps: LegacyActionsDeps
): Pick<IntentActions, 'selectPlace' | 'selectAddress' | 'exploreFromSuggest'> {
	const pathFor = (filters: PropertyFilters) =>
		cleanLocationQueryFromPath(deps.api.getPathFromFilters(filters));
	return {
		selectPlace: place =>
			deps.navigate(pathFor({ states: [place.state], ...pointOf(place) })),
		selectAddress(place) {
			const path = pathFor({
				states: [place.state],
				macroregions: place.macroregions,
				cities: place.cities,
				regions: place.regions,
				...pointOf(place)
			});
			const macroregions = place.macroregions?.join(',');
			return deps.navigate(
				macroregions ? { path, query: { macroregions } } : path
			);
		},
		// Production ignores the first field here and opens the default search.
		exploreFromSuggest: () => deps.navigate(deps.api.DEFAULT_URL_PATH)
	};
}

// Production only names the address when both coordinates are truthy.
function pointOf(place: PlaceSelection): PropertyFilters {
	if (!place.lat || !place.lng) return {};
	return {
		poiLocationLat: place.lat,
		poiLocationLng: place.lng,
		addressSlug: place.text
	};
}
